import { VimSDKProvider } from '@getvim/internal-vim-os-sdk/react';
import { ApolloClient, ApolloProvider, NormalizedCacheObject } from '@apollo/client';
import { AnalyticsHover as AnalyticsHoverProvider, ErrorBoundaryWrapper } from '@getvim/atomic-ui';
import { BaseWidgetAnalyticsEventTypes } from '@getvim/react-app-infra';
import React from 'react';
import ReactDOM from 'react-dom';
import { IntlProvider } from 'react-intl';
import { GraphqlClient } from './api/graphqlClient';
import App from './App';
import { widgetId } from './consts';
import { FeatureFlagProvider, ffClientOptions } from './utils/featureFlags';
import { eligibilityLogger } from './utils/logger';
import sdkOptions from './sdkOptions';
import { analyticsManager } from './utils/analyticManager';
import { useAnalytics } from './hooks/useAnalytics';
import { AppEnv } from './utils/env';
import { TelemetryProvider } from '@getvim/opentelemetry-sdk/react';
import { RequiredStateWrapper } from './atomic/RequiredStateWrapper';
import { ConfigProvider } from './context';

const handleRenderError = ({ error }) => {
  eligibilityLogger.error('Client application crashed due to render error', {
    error,
  });
};

const handleHoverStart = () => {
  analyticsManager.track(BaseWidgetAnalyticsEventTypes.appHoverStart, {
    app_name: widgetId,
    is_scroll_available: document.body.scrollHeight > document.body.clientHeight,
  });
};

const handleHoverEnd = (time) => {
  analyticsManager.track(BaseWidgetAnalyticsEventTypes.appHoverEnd, {
    app_name: widgetId,
    app_hover_opened_for_time: time,
  });
};

const AnalyticsHoverProviderWrapper = ({ children }) => {
  return (
    <AnalyticsHoverProvider
      className="app-hover"
      onHoverAction={handleHoverStart}
      onLeaveAction={handleHoverEnd}
    >
      {children}
    </AnalyticsHoverProvider>
  );
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const appEnv: AppEnv = (window as any).$vim_environment?.APP_ENV;

const AnalyticsWrapper = ({ children }) => {
  const analyticsReady = useAnalytics();
  return analyticsReady ? children : null;
};

const Index = () => {
  return (
    <TelemetryProvider
      options={{
        serviceName: widgetId,
        env: appEnv,
        enableLogs: true,
        enableMetrics: true,
        enableTraces: true,
      }}
    >
      <VimSDKProvider {...sdkOptions}>
        <RequiredStateWrapper>
          <React.StrictMode>
            <IntlProvider locale="en">
              <FeatureFlagProvider {...ffClientOptions}>
                <ApolloProvider
                  client={
                    GraphqlClient.eligibilityBFFClient as unknown as ApolloClient<NormalizedCacheObject>
                  }
                >
                  <ConfigProvider>
                    <ErrorBoundaryWrapper
                      onRenderError={handleRenderError}
                      applicationDisplayName={widgetId}
                    >
                      <AnalyticsWrapper>
                        <AnalyticsHoverProviderWrapper>
                          <App />
                        </AnalyticsHoverProviderWrapper>
                      </AnalyticsWrapper>
                    </ErrorBoundaryWrapper>
                  </ConfigProvider>
                </ApolloProvider>
              </FeatureFlagProvider>
            </IntlProvider>
          </React.StrictMode>
        </RequiredStateWrapper>
      </VimSDKProvider>
    </TelemetryProvider>
  );
};

ReactDOM.render(<Index />, document.getElementById('root'));
